import { ColumnDefinition, ColumnDefinitions, ColumnGroup } from './column-group.class';
import { Injectable } from '@angular/core';
import { TranslationsService } from '../services/translations.service';
import {
  noNegativeYesPositiveCssClass,
  missingNumberAsNull,
  sortPositiveValuesWithMissingData,
  valueOrTranslateMissingData,
  percentageOrMissingData,
  missingValueAsNull,
  sortDatesWithMissingValues,
  negativeOneAsNa,
  negativeOneAsNull,
} from '../helpers/data.helpers';
import { CommonService } from '../services/common.service';
import { courseStatusCssClass } from '../helpers/courses.helpers';
import {
  convertMentorPlaylistRatingToStatus,
  ficoScoreCssClass,
  mentorPlaylistCssClass,
} from '../helpers/mentor.helpers';
import { booleanDataMap } from '../data/data-maps/generic.data';

@Injectable({ providedIn: 'root' })
export class MentorColumns extends ColumnGroup {
  public constructor(private translations: TranslationsService, private commonService: CommonService) {
    super();
  }

  protected columnDefinitions: ColumnDefinitions = {
    totalDaysZeroTrips: {
      width: 140,
      filterValueGetter: (params) => missingNumberAsNull(params.data.totalDaysZeroTrips),
      comparator: (valueA, valueB) => sortPositiveValuesWithMissingData(valueA, valueB),
      valueFormatter: (params) => valueOrTranslateMissingData(params.value, this.translations),
    },
    totalDaysWithTrips: {
      width: 120,
      filterValueGetter: (params) => missingNumberAsNull(params.data.totalDaysWithTrips),
      comparator: (valueA, valueB) => sortPositiveValuesWithMissingData(valueA, valueB),
      valueFormatter: (params) => valueOrTranslateMissingData(params.value, this.translations),
    },
    distanceTravelledMiles: {
      width: 150,
      filterValueGetter: (params) => missingNumberAsNull(params.data.distanceTravelledMiles),
      comparator: (valueA, valueB) => sortPositiveValuesWithMissingData(valueA, valueB),
      valueFormatter: (params) => valueOrTranslateMissingData(params.value, this.translations),
    },
    distanceTravelledKilometres: {
      width: 140,
      filterValueGetter: (params) => missingNumberAsNull(params.data.distanceTravelledKilometres),
      comparator: (valueA, valueB) => sortPositiveValuesWithMissingData(valueA, valueB),
      valueFormatter: (params) => valueOrTranslateMissingData(params.value, this.translations),
    },
    totalPassengerTrips: {
      width: 150,
      filterValueGetter: (params) => missingNumberAsNull(params.data.totalPassengerTrips),
      comparator: (valueA, valueB) => sortPositiveValuesWithMissingData(valueA, valueB),
      valueFormatter: (params) => valueOrTranslateMissingData(params.value, this.translations),
    },
    overallScore: {
      width: 130,
      filterValueGetter: (params) => missingNumberAsNull(params.data.overallScore),
      comparator: (valueA, valueB) => sortPositiveValuesWithMissingData(valueA, valueB),
      valueFormatter: (params) => valueOrTranslateMissingData(params.value, this.translations),
      cellClass: (params) => ficoScoreCssClass(params.value),
    },
    totalTripsDriver: {
      width: 120,
      filterValueGetter: (params) => missingNumberAsNull(params.data.totalTripsDriver),
      comparator: (valueA, valueB) => sortPositiveValuesWithMissingData(valueA, valueB),
      valueFormatter: (params) => valueOrTranslateMissingData(params.value, this.translations),
    },
    totalPersonalTrips: {
      width: 140,
      filterValueGetter: (params) => missingNumberAsNull(params.data.totalPersonalTrips),
      comparator: (valueA, valueB) => sortPositiveValuesWithMissingData(valueA, valueB),
      valueFormatter: (params) => valueOrTranslateMissingData(params.value, this.translations),
    },
    percPassengerTrips: {
      width: 120,
      filterValueGetter: (params) => missingNumberAsNull(params.data.percPassengerTrips),
      comparator: (valueA, valueB) => sortPositiveValuesWithMissingData(valueA, valueB),
      valueFormatter: (params) => percentageOrMissingData(params.value, this.translations),
    },
    percPersonalTrips: {
      width: 120,
      filterValueGetter: (params) => missingNumberAsNull(params.data.percPersonalTrips),
      comparator: (valueA, valueB) => sortPositiveValuesWithMissingData(valueA, valueB),
      valueFormatter: (params) => percentageOrMissingData(params.value, this.translations),
    },
    mentorStartDate: {
      width: 110,
    },
    mentorLogin: {
      width: 120,
      filterValueGetter: (params) => missingValueAsNull(params.data.mentorLogin),
      comparator: (valueA, valueB) => sortDatesWithMissingValues(valueA, valueB),
    },
    currentMentorEntitlement: {
      width: 150,
    },
    mentorErsActive: () => ({
      width: 120,
      refData: this.translations.translateDataMap(booleanDataMap),
      cellClass: (params) => noNegativeYesPositiveCssClass(params.api.getValue('mentorErsActive', params.node)),
    }),
    mentorInsightLogin: {
      width: 140,
      filterValueGetter: (params) => missingValueAsNull(params.data.mentorInsightLogin),
      comparator: (valueA, valueB) => sortDatesWithMissingValues(valueA, valueB),
    },
    mentorInsightSOSActive: () => ({
      width: 150,
      refData: this.translations.translateDataMap(booleanDataMap),
      cellClass: (params) => noNegativeYesPositiveCssClass(params.api.getValue('mentorInsightSOSActive', params.node)),
    }),
    workHoursActive: {
      width: 120,
      cellClass: (params) => noNegativeYesPositiveCssClass(params.value),
      valueFormatter: (params) => this.translations.translate(`REPORTDATA.${params.value.toLowerCase()}`, params.value),
    },
    playlist: {
      width: 140,
      filterValueGetter: (params) => {
        let rating = convertMentorPlaylistRatingToStatus(params.data.playlist);
        return this.translations.translate(`REPORTDATA.${rating}`, rating);
      },
      valueFormatter: (params) => {
        let rating = convertMentorPlaylistRatingToStatus(params.value);
        return this.translations.translate(`REPORTDATA.${rating}`, rating);
      },
      cellClass: (params) => mentorPlaylistCssClass(params.api.getValue('playlist', params.node)),
    },
    playlistOutstanding: {
      width: 150,
      filterValueGetter: (params) => negativeOneAsNull(params.data.playlistOutstanding),
      valueFormatter: (params) => {
        const value = negativeOneAsNa(params.value);
        return this.translations.translate(`REPORTDATA.${value}`, value);
      },
    },
    trainingAssigned: {
      width: 140,
      filterValueGetter: (params) => negativeOneAsNull(params.data.trainingAssigned),
      valueFormatter: (params) => {
        const value = negativeOneAsNa(params.value);
        return this.translations.translate(`REPORTDATA.${value}`, value);
      },
    },
    trainingComplete: {
      width: 140,
      filterValueGetter: (params) => negativeOneAsNull(params.data.trainingComplete),
      valueFormatter: (params) => {
        const value = negativeOneAsNa(params.value);
        return this.translations.translate(`REPORTDATA.${value}`, value);
      },
    },
    lastTripDate: {
      width: 100,
      filterValueGetter: (params) => missingValueAsNull(params.data.lastTripDate),
      comparator: (valueA, valueB) => sortDatesWithMissingValues(valueA, valueB),
      valueFormatter: (params) => valueOrTranslateMissingData(params.value, this.translations),
    },
    previousMonthDistanceTravelledReported: {
      width: 230,
    },
    totalMovementTripHours: {
      width: 110,
    },
    totalTripMiles: {
      width: 110,
    },
  };
}
